import React from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-10">Last Updated: April 1, 2026</p>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-3">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              Eventism LLC ("Eventism", "we", "us") respects your privacy and is committed to protecting your 
              personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use our event services marketplace platform. This policy applies to all users 
              including clients, vendors, staffing professionals, and visitors.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. Information We Collect</h2>

            <h3 className="text-lg font-medium mt-4 mb-2">Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>Account Information:</strong> Name, email address, phone number, password, and role (client, vendor, staff)</li>
              <li><strong>Event Details:</strong> Event date, time, location, guest count, service preferences, and special requirements</li>
              <li><strong>Payment Information:</strong> Credit card details (processed and stored securely by Stripe — we do not store your full card number)</li>
              <li><strong>Vendor Profiles:</strong> Business name, service descriptions, pricing, photos, availability, and certifications</li>
              <li><strong>Communications:</strong> Messages, reviews, and support requests you send through the platform</li>
            </ul>

            <h3 className="text-lg font-medium mt-4 mb-2">Information Collected Automatically</h3>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the platform, and referral sources</li>
              <li><strong>Device Information:</strong> Browser type, operating system, IP address, and device identifiers</li>
              <li><strong>Location Data:</strong> Approximate location based on IP address or ZIP code you provide for event planning</li>
              <li><strong>Cookies:</strong> We use cookies and similar technologies to maintain sessions, remember preferences, and analyze usage patterns</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">We use your information to:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Provide, operate, and maintain the Eventism platform</li>
              <li>Process bookings, payments, and refunds</li>
              <li>Match you with appropriate vendors and service providers based on your event needs</li>
              <li>Send booking confirmations, reminders, and service updates</li>
              <li>Communicate about new features, promotions, and relevant event services</li>
              <li>Improve our platform through analytics and user feedback</li>
              <li>Prevent fraud, enforce our terms, and protect our users</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. How We Share Your Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>Service Providers:</strong> When you book a service, we share relevant event details (date, location, guest count, requirements) with the assigned vendor to fulfill your booking</li>
              <li><strong>Payment Processors:</strong> Stripe processes your payment information securely under their own privacy policy</li>
              <li><strong>Analytics Partners:</strong> Aggregated, anonymized data may be shared with analytics services to improve our platform</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law, subpoena, or to protect the rights and safety of our users</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We do <strong>not</strong> sell your personal information to third parties for marketing purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. Data Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement industry-standard security measures to protect your personal data, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-3">
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure password hashing (bcrypt)</li>
              <li>PCI-compliant payment processing through Stripe</li>
              <li>Regular security audits and access controls</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              While we take reasonable measures to protect your information, no method of electronic transmission 
              or storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We retain your personal data for as long as your account is active or as needed to provide services. 
              After account deletion, we may retain certain information for up to 3 years for legal, accounting, 
              and dispute resolution purposes. Anonymized analytics data may be retained indefinitely.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">7. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements)</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
              <li><strong>Data Portability:</strong> Request your data in a structured, machine-readable format</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              To exercise these rights, contact us at{' '}
              <a href="mailto:team@eventism.com" className="text-indigo-500 hover:underline">team@eventism.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">8. Colorado Privacy Act Compliance</h2>
            <p className="text-muted-foreground leading-relaxed">
              As a Colorado-based company, we comply with the Colorado Privacy Act (CPA). Colorado residents have 
              additional rights including the right to opt out of targeted advertising and the right to appeal 
              a decision regarding a privacy rights request. To exercise these rights or submit an appeal, 
              email us at{' '}
              <a href="mailto:team@eventism.com" className="text-indigo-500 hover:underline">team@eventism.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">9. Cookies</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use essential cookies to maintain your login session and preferences. We may also use analytics 
              cookies to understand how users interact with our platform. You can manage cookie preferences through 
              your browser settings. Disabling essential cookies may affect platform functionality.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">10. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              Eventism is not intended for use by individuals under 18 years of age. We do not knowingly collect 
              personal information from children. If we become aware that we have collected data from a child, 
              we will take steps to delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">11. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. Material changes will be communicated via email 
              or a notice on the platform. The "Last Updated" date at the top reflects the most recent revision. 
              Continued use of the Service after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">12. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              For questions or concerns about this Privacy Policy or our data practices:
            </p>
            <div className="mt-3 text-muted-foreground">
              <p className="font-medium text-foreground">Eventism LLC</p>
              <p>Denver, Colorado</p>
              <p>Email: <a href="mailto:team@eventism.com" className="text-indigo-500 hover:underline">team@eventism.com</a></p>
              <p>Phone: <a href="tel:7209074044" className="text-indigo-500 hover:underline">(720) 907-4044</a></p>
            </div>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t text-center">
          <p className="text-sm text-muted-foreground">
            See also our <Link to="/terms" className="text-indigo-500 hover:underline">Terms of Service</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
